"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../lib/api";
import { cartKey, defaultAddress, detectShopCountryFromBrowser, itemRequiresShipping, maxQuantity, primaryImage, toQuoteItem } from "../lib/shop";
import { ShopCartDrawer } from "./ShopCartDrawer";

const STORAGE_KEY = "nitro_shop_cart_v1";
const ShopCartContext = createContext(null);

export function ShopCartProvider({ children }) {
  const [countryIso2, setCountryIso2State] = useState("CO");
  const [cart, setCart] = useState([]);
  const [quote, setQuote] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(defaultAddress("CO"));
  const [selectedPaymentMethodCode, setSelectedPaymentMethodCode] = useState("");
  const [selectedShippingMethodCode, setSelectedShippingMethodCode] = useState("");
  const [state, setState] = useState({ checkingOut: false, error: "", notice: "" });

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
      setCountryIso2State(detectShopCountryFromBrowser());
      if (Array.isArray(saved?.cart)) setCart(saved.cart);
      setShippingAddress(defaultAddress(detectShopCountryFromBrowser()));
    } catch {
      setCountryIso2State(detectShopCountryFromBrowser());
      setCart([]);
    } finally {
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    if (!isReady) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ countryIso2, cart }));
    window.dispatchEvent(new CustomEvent("nitro-shop-cart-updated", { detail: { countryIso2, cart } }));
  }, [cart, countryIso2, isReady]);

  useEffect(() => {
    setShippingAddress((current) => ({ ...current, countryIso2 }));
  }, [countryIso2]);

  useEffect(() => {
    let active = true;
    if (!cart.length) {
      setQuote(null);
      return () => {
        active = false;
      };
    }

    apiFetch("/api/v1/shop/quote", {
      method: "POST",
      body: JSON.stringify({
        countryIso2,
        items: cart.map(toQuoteItem),
        paymentMethodCode: selectedPaymentMethodCode || undefined,
        shippingMethodCode: selectedShippingMethodCode || undefined,
      }),
      timeoutMs: 10000,
    })
      .then((data) => {
        if (active) setQuote(data);
      })
      .catch((error) => {
        if (active) setQuote({ ok: false, errors: [error.message], items: [], totals: null, paymentMethods: [] });
      });

    return () => {
      active = false;
    };
  }, [cart, countryIso2, selectedPaymentMethodCode, selectedShippingMethodCode]);

  useEffect(() => {
    if (!quote) return;
    if (!selectedPaymentMethodCode && quote.selectedPaymentMethod?.code) {
      setSelectedPaymentMethodCode(quote.selectedPaymentMethod.code);
    }
    if (!selectedShippingMethodCode && quote.selectedShippingMethod?.code) {
      setSelectedShippingMethodCode(quote.selectedShippingMethod.code);
    }
  }, [quote, selectedPaymentMethodCode, selectedShippingMethodCode]);

  const cartCount = useMemo(() => cart.reduce((total, item) => total + item.quantity, 0), [cart]);
  const selectedPayment = quote?.selectedPaymentMethod || quote?.paymentMethods?.[0] || null;
  const selectedShipping = quote?.selectedShippingMethod || quote?.shippingMethods?.[0] || null;

  const setCountryIso2 = useCallback(function setCountryIso2(nextCountry) {
    setCountryIso2State(nextCountry);
  }, []);

  const addProduct = useCallback(function addProduct(product, variant = null, quantity = 1) {
    const key = cartKey(product.slug, variant?.slug || "");
    const safeQuantity = Math.max(1, Math.min(Number(quantity || 1), maxQuantity(variant)));
    setState({ checkingOut: false, error: "", notice: `${product.name}${variant ? ` / ${variant.name}` : ""} agregado al carrito.` });
    setCart((current) => {
      const existing = current.find((item) => item.key === key);
      if (existing) {
        return current.map((item) => item.key === key ? { ...item, quantity: Math.min(item.quantity + safeQuantity, item.maxQuantity || 9) } : item);
      }
      return [...current, {
        key,
        productSlug: product.slug,
        variantSlug: variant?.slug || "",
        name: product.name,
        variantName: variant?.name || "",
        type: product.type,
        imageUrl: primaryImage(product, variant),
        requiresShipping: itemRequiresShipping(product, variant),
        quantity: safeQuantity,
        maxQuantity: maxQuantity(variant),
      }];
    });
    setIsDrawerOpen(true);
  }, []);

  function updateQuantity(key, quantity) {
    const next = Number(quantity);
    if (!next) {
      setCart((current) => current.filter((item) => item.key !== key));
      return;
    }
    setCart((current) => current.map((item) => item.key === key ? { ...item, quantity: Math.min(Math.max(next, 1), item.maxQuantity || 9) } : item));
  }

  function removeLine(key) {
    setCart((current) => current.filter((item) => item.key !== key));
  }

  function clearCart() {
    setCart([]);
    setQuote(null);
    setState({ checkingOut: false, error: "", notice: "" });
  }

  async function checkout(options = {}) {
    setState({ checkingOut: true, error: "", notice: "" });
    try {
      const payload = {
        countryIso2,
        items: cart.map(toQuoteItem),
        paymentMethodCode: selectedPaymentMethodCode || selectedPayment?.code || undefined,
        shippingMethodCode: selectedShippingMethodCode || selectedShipping?.code || undefined,
        shippingAddress: quote?.requiresShipping ? shippingAddress : null,
        customer: options.customer || undefined,
      };
      const data = await apiFetch("/api/v1/shop/checkout", {
        method: "POST",
        body: JSON.stringify(payload),
        timeoutMs: 15000,
      });
      const payment = data.payments?.[0];
      if (payment?.provider === "wompi") {
        if (payment.paymentUrl || payment.response?.checkout) {
          window.location.href = payment.paymentUrl || `/pagos/wompi?reference=${encodeURIComponent(data.order.orderNumber)}`;
          return;
        }
        setState({
          checkingOut: false,
          error: payment.response?.message || "El pago Wompi no esta inicializado. Revisa las credenciales de Wompi en el admin de tienda.",
          notice: "",
        });
        return;
      }
      if (payment?.provider === "paypal" && payment.paymentUrl) {
        window.location.href = payment.paymentUrl;
        return;
      }
      setState({ checkingOut: false, error: "", notice: "Orden creada. El pago queda pendiente de configuracion o revision." });
    } catch (error) {
      if (error.status === 401 || quote?.requiresAuth) {
        window.location.href = `/login?redirect=${encodeURIComponent("/tienda/carrito")}`;
        return;
      }
      const details = Array.isArray(error.details) ? ` ${error.details.join(" ")}` : "";
      setState({ checkingOut: false, error: `${error.message || "No fue posible crear la orden."}${details}`, notice: "" });
    }
  }

  const value = useMemo(() => ({
    cart,
    cartCount,
    checkout,
    clearCart,
    countryIso2,
    isDrawerOpen,
    isReady,
    openCart: () => setIsDrawerOpen(true),
    closeCart: () => setIsDrawerOpen(false),
    addProduct,
    quote,
    removeLine,
    selectedPayment,
    selectedPaymentMethodCode,
    selectedShipping,
    selectedShippingMethodCode,
    setCountryIso2,
    setSelectedPaymentMethodCode,
    setSelectedShippingMethodCode,
    shippingAddress,
    setShippingAddress,
    state,
    updateQuantity,
  }), [addProduct, cart, cartCount, countryIso2, isDrawerOpen, isReady, quote, selectedPayment, selectedPaymentMethodCode, selectedShipping, selectedShippingMethodCode, setCountryIso2, shippingAddress, state]);

  return (
    <ShopCartContext.Provider value={value}>
      {children}
      <ShopCartDrawer />
    </ShopCartContext.Provider>
  );
}

export function useShopCart() {
  const context = useContext(ShopCartContext);
  if (!context) throw new Error("useShopCart debe usarse dentro de ShopCartProvider.");
  return context;
}
